/**
 * 恢复计数管理 Composable
 * 管理今日恢复人数的计算和持久化
 */

export default function useRecoveryCount() {
  return {
    data() {
      return {
        recoveryPersist: { date: '', timeBasedCount: 0 },
        hourlyRecoveryTimer: null,
        hourlyRecoveryStarter: null,
        midnightResetTimer: null
      };
    },

    methods: {
      /**
       * 计算今日恢复人数
       * 真实人数：只统计计划开始时间 <= 当前时间（已开始或已结束）的预约
       * 展示值：真实人数 + 时间自增值（8:00～17:00 满小时每小时 +2）
       */
      calculateTodayRecoveryCount() {
        const now = Date.now();
        let realCount = 0;
        if (Array.isArray(this.todayAppointmentData)) {
          realCount = this.todayAppointmentData.filter((item) => {
            const date = item.appointmentDate ?? item.appointment_date;
            const time = item.startTime ?? item.start_time;
            if (!date || !time) return false;
            const timePart = (typeof time === 'string' && time.length >= 5) ? time.substring(0, 5) : time;
            const plannedStart = new Date(`${date}T${timePart}:00`).getTime();
            return Number.isFinite(plannedStart) && plannedStart <= now;
          }).length;
        }
        const timeBasedCount = this.calculateTimeBasedCount();
        return realCount + timeBasedCount;
      },

      /**
       * 计算基于时间的人数增长
       */
      calculateTimeBasedCount() {
        const todayStr = this.getTodayStr();
        if (this.recoveryPersist && this.recoveryPersist.date === todayStr) {
          return this.recoveryPersist.timeBasedCount;
        }
        return this.calculateTimeBasedCountFromClock();
      },

      /**
       * 从时钟计算基于时间的人数（8点+2，9点+2，…，17点最后一次+2，17点之后不再增加，共10次×2=20）
       */
      calculateTimeBasedCountFromClock() {
        const h = new Date().getHours();
        return (h < 8) ? 0 : (h >= 17) ? 20 : (h - 8 + 1) * 2;
      },

      /**
       * 获取今日日期字符串
       */
      getTodayStr() {
        const now = new Date();
        const y = now.getFullYear();
        const m = String(now.getMonth() + 1).padStart(2, '0');
        const d = String(now.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      },

      /**
       * 获取恢复计数的存储键
       */
      getRecoveryStorageKey() {
        const id = this.effectiveStoreId ? String(this.effectiveStoreId) : 'default';
        return `dailyRecoveryCount-${id}`;
      },

      /**
       * 初始化恢复计数持久化
       */
      initRecoveryPersist() {
        try {
          const key = this.getRecoveryStorageKey();
          const raw = localStorage.getItem(key);
          const today = this.getTodayStr();
          if (raw) {
            const obj = JSON.parse(raw);
            if (obj && obj.date === today && typeof obj.timeBasedCount === 'number') {
              this.recoveryPersist = { date: obj.date, timeBasedCount: obj.timeBasedCount };
            } else {
              this.recoveryPersist = { date: today, timeBasedCount: 0 };
              localStorage.setItem(key, JSON.stringify(this.recoveryPersist));
            }
          } else {
            this.recoveryPersist = { date: today, timeBasedCount: 0 };
            localStorage.setItem(key, JSON.stringify(this.recoveryPersist));
          }
        } catch (e) {
          this.recoveryPersist = { date: this.getTodayStr(), timeBasedCount: 0 };
        }
        this.recalcAndPersistTimeBasedCount();
        this.setupHourlyUpdateTimer();
        this.setupMidnightResetTimer();
      },

      /**
       * 重新计算并持久化基于时间的计数
       */
      recalcAndPersistTimeBasedCount() {
        const today = this.getTodayStr();
        const count = this.calculateTimeBasedCountFromClock();
        this.recoveryPersist = { date: today, timeBasedCount: count };
        try {
          localStorage.setItem(this.getRecoveryStorageKey(), JSON.stringify(this.recoveryPersist));
        } catch (e) {}
      },

      /**
       * 设置每小时更新定时器
       */
      setupHourlyUpdateTimer() {
        if (this.hourlyRecoveryTimer) {
          clearInterval(this.hourlyRecoveryTimer);
        }
        if (this.hourlyRecoveryStarter) {
          clearTimeout(this.hourlyRecoveryStarter);
        }
        const now = new Date();
        const starter = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours() + 1, 0, 0, 0);
        const delay = Math.max(0, starter.getTime() - now.getTime());
        this.hourlyRecoveryStarter = setTimeout(() => {
          this.recalcAndPersistTimeBasedCount();
          this.hourlyRecoveryTimer = setInterval(() => {
            this.recalcAndPersistTimeBasedCount();
          }, 60 * 60 * 1000);
        }, delay);
      },

      /**
       * 设置午夜重置定时器
       */
      setupMidnightResetTimer() {
        if (this.midnightResetTimer) {
          clearTimeout(this.midnightResetTimer);
        }
        const now = new Date();
        const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
        const delay = Math.max(0, nextMidnight.getTime() - now.getTime());
        this.midnightResetTimer = setTimeout(() => {
          this.resetDailyRecovery();
          this.setupMidnightResetTimer();
        }, delay);
      },

      /**
       * 重置每日恢复计数
       */
      resetDailyRecovery() {
        const today = this.getTodayStr();
        this.recoveryPersist = { date: today, timeBasedCount: 0 };
        try {
          localStorage.setItem(this.getRecoveryStorageKey(), JSON.stringify(this.recoveryPersist));
        } catch (e) {}
        this.recalcAndPersistTimeBasedCount();
        this.setupHourlyUpdateTimer();
      },

      /**
       * 启动每小时恢复计数更新（兼容方法）
       */
      startHourlyRecovery() {
        this.setupHourlyUpdateTimer();
      },

      /**
       * 在午夜重置恢复计数（兼容方法）
       */
      resetRecoveryAtMidnight() {
        this.setupMidnightResetTimer();
      }
    }
  };
}

